import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Import actions
import {
    createRazorpaySetting,
    getAllRazorpaySettings,
    updateRazorpaySetting,
    createCcavenueSetting,
    getAllCcavenueSettings,
    updateCcavenueSetting,
    createCashfreeSetting,
    getAllCashfreeSettings,
    updateCashfreeSetting,
    createCashfreePayoutSetting,
    getAllCashfreePayoutSettings,
    updateCashfreePayoutSetting,
    createRazorpayPayoutSetting,
    getAllRazorpayPayoutSettings,
    updateRazorpayPayoutSetting,
    resetPaymentState
} from '../../actions/paymentSettingActions';
import { Link } from 'react-router-dom'; 
// MessageModal Component
import MessageModal from '../Pages/MessageModel';

const PaymentSettings = () => {
    const dispatch = useDispatch();

    // Get state from Redux store
    const paymentState = useSelector(state => state.paymentSetting);
    const {
        razorpaySettings,
        ccavenueSettings,
        cashfreeSettings,
        cashfreePayoutSettings,
        razorpayPayoutSettings,
        razorpayLoading,
        ccavenueLoading,
        cashfreeLoading,
        cashfreePayoutLoading,
        razorpayPayoutLoading,
        razorpayError,
        ccavenueError,
        cashfreeError,
        cashfreePayoutError,
        razorpayPayoutError
    } = paymentState;

    // ==================== UTILITY FUNCTIONS ====================

    const getHospitalId = useCallback(() => {
        const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
        return loginData.hospitalID || '0';
    }, []);

    // Initial Hospital ID
    const initialHospitalId = getHospitalId(); 

    // Main tabs state
    const [activeMainTab, setActiveMainTab] = useState('payment');
    const [activePaymentTab, setActivePaymentTab] = useState('razorpay');
    const [activePayoutTab, setActivePayoutTab] = useState('razorpay');

    // Form states for each payment gateway - Initialized with hospital ID
    const [razorpayForm, setRazorpayForm] = useState({
        field1: "", // url
        field2: "", // key_id
        field3: "", // key_secret
        field4: "0", // status
        field5: "", // webhook_secret
        hid: initialHospitalId // Use initialHospitalId
    });

    const [cashfreeForm, setCashfreeForm] = useState({
        field1: "", // app_id
        field2: "", // secret_key
        field3: "0", // status
        hid: initialHospitalId // Use initialHospitalId
    });

    const [ccavenueForm, setCcavenueForm] = useState({
        field1: "", // merchant_id
        field2: "", // access_code
        field3: "", // working_key
        field4: "0", // status
        hid: initialHospitalId // Use initialHospitalId
    });

    // Form states for payout
    const [cashfreePayoutForm, setCashfreePayoutForm] = useState({
        field1: "", // client_id
        field2: "", // client_secret
        field3: "0", // status
        hid: initialHospitalId // Use initialHospitalId
    });

    const [razorpayPayoutForm, setRazorpayPayoutForm] = useState({
        field1: "", // url
        field2: "", // key_id
        field3: "", // key_secret
        field4: "0", // status
        field5: "", // webhook_secret
        hid: initialHospitalId // Use initialHospitalId
    });

    // Message Modal states
    const [modalShow, setModalShow] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [modalVariant, setModalVariant] = useState("info");
    const [modalLoading, setModalLoading] = useState(false);

    // ==================== MESSAGE MODAL FUNCTIONS ====================

    const showMessage = useCallback((title, message, variant = "info") => {
        setModalTitle(title);
        setModalMessage(message);
        setModalVariant(variant);
        setModalShow(true);
    }, []);

    const showSuccess = useCallback((message) => {
        showMessage("Success", message, "success");
    }, [showMessage]);

    const showError = useCallback((message) => {
        showMessage("Error", message, "danger");
    }, [showMessage]);

    const handleModalClose = useCallback(() => {
        setModalShow(false);
        setModalLoading(false);
    }, []);

    // ==================== DATA LOADING ====================

    const loadPaymentSettings = useCallback(() => {
        const hospitalId = getHospitalId();

        // Load all payment settings
        dispatch(getAllRazorpaySettings(hospitalId));
        dispatch(getAllCashfreeSettings(hospitalId));
        dispatch(getAllCcavenueSettings(hospitalId));
        dispatch(getAllCashfreePayoutSettings(hospitalId));
        dispatch(getAllRazorpayPayoutSettings(hospitalId));
    }, [dispatch, getHospitalId]);

    // ==================== EFFECT HOOKS ====================

    // Initial data load and cleanup on unmount
    useEffect(() => {
        loadPaymentSettings();
        return () => {
            dispatch(resetPaymentState());
        };
    }, [dispatch, loadPaymentSettings]);

    // Update form states when Redux state changes (Auto-populate logic)
    useEffect(() => {
        if (razorpaySettings && razorpaySettings.length > 0) {
            const latestRazorpay = razorpaySettings[0]; // Assuming latest is first or only one
            setRazorpayForm(prev => ({
                ...prev,
                field1: latestRazorpay.field1 || "",
                field2: latestRazorpay.field2 || "",
                field3: latestRazorpay.field3 || "",
                field4: String(latestRazorpay.field4) || "0", // Ensure status is a string
                field5: latestRazorpay.field5 || "",
                hid: latestRazorpay.hid || initialHospitalId
            }));
        }
    }, [razorpaySettings, initialHospitalId]);

    useEffect(() => {
        if (cashfreeSettings && cashfreeSettings.length > 0) {
            const latestCashfree = cashfreeSettings[0];
            setCashfreeForm(prev => ({
                ...prev,
                field1: latestCashfree.field1 || "",
                field2: latestCashfree.field2 || "",
                field3: String(latestCashfree.field3) || "0", // Ensure status is a string
                hid: latestCashfree.hid || initialHospitalId
            }));
        }
    }, [cashfreeSettings, initialHospitalId]);

    useEffect(() => {
        if (ccavenueSettings && ccavenueSettings.length > 0) {
            const latestCcavenue = ccavenueSettings[0];
            setCcavenueForm(prev => ({
                ...prev,
                field1: latestCcavenue.field1 || "",
                field2: latestCcavenue.field2 || "",
                field3: latestCcavenue.field3 || "",
                field4: String(latestCcavenue.field4) || "0", // Ensure status is a string
                hid: latestCcavenue.hid || initialHospitalId
            }));
        }
    }, [ccavenueSettings, initialHospitalId]);

    useEffect(() => {
        if (cashfreePayoutSettings && cashfreePayoutSettings.length > 0) {
            const latestCashfreePayout = cashfreePayoutSettings[0];
            setCashfreePayoutForm(prev => ({
                ...prev,
                field1: latestCashfreePayout.field1 || "",
                field2: latestCashfreePayout.field2 || "",
                field3: String(latestCashfreePayout.field3) || "0", // Ensure status is a string
                hid: latestCashfreePayout.hid || initialHospitalId
            }));
        }
    }, [cashfreePayoutSettings, initialHospitalId]);

    useEffect(() => {
        if (razorpayPayoutSettings && razorpayPayoutSettings.length > 0) {
            const latestRazorpayPayout = razorpayPayoutSettings[0];
            setRazorpayPayoutForm(prev => ({
                ...prev,
                field1: latestRazorpayPayout.field1 || "",
                field2: latestRazorpayPayout.field2 || "",
                field3: latestRazorpayPayout.field3 || "",
                field4: String(latestRazorpayPayout.field4) || "0", // Ensure status is a string
                field5: latestRazorpayPayout.field5 || "",
                hid: latestRazorpayPayout.hid || initialHospitalId
            }));
        }
    }, [razorpayPayoutSettings, initialHospitalId]);

    // ==================== HANDLER FUNCTIONS ====================

    const handleRazorpayChange = useCallback((field, value) => {
        setRazorpayForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCashfreeChange = useCallback((field, value) => {
        setCashfreeForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCcavenueChange = useCallback((field, value) => {
        setCcavenueForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCashfreePayoutChange = useCallback((field, value) => {
        setCashfreePayoutForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleRazorpayPayoutChange = useCallback((field, value) => {
        setRazorpayPayoutForm(prev => ({ ...prev, [field]: value }));
    }, []);

    // ==================== SUBMISSION HANDLERS ====================

    // Generic submit helper function
    const handleSubmit = async (e, settings, formData, createAction, updateAction, successMsg, errorMsg) => {
        e.preventDefault();
        setModalLoading(true);

        try {
            const hospitalId = getHospitalId();
            const dataToSubmit = {
                ...formData,
                hid: hospitalId
            };

            // Check if settings exist (using settings[0].id for simplicity, assuming API returns only one or the latest at index 0)
            if (settings && settings.length > 0 && settings[0].id) {
                await dispatch(updateAction({
                    ...dataToSubmit,
                    id: settings[0].id
                }));
            } else {
                await dispatch(createAction(dataToSubmit));
            }

            showSuccess(successMsg);
            loadPaymentSettings(); // Refresh data after successful save/update
        } catch (error) {
            showError(`${errorMsg}: ${error.message}`);
        } finally {
            setModalLoading(false);
        }
    };

    const handleRazorpaySubmit = (e) => 
        handleSubmit(
            e, 
            razorpaySettings, 
            razorpayForm, 
            createRazorpaySetting, 
            updateRazorpaySetting, 
            'Razorpay Payment settings saved successfully!', 
            'Failed to save Razorpay Payment settings'
        );

    const handleCashfreeSubmit = (e) =>
        handleSubmit(
            e,
            cashfreeSettings,
            cashfreeForm,
            createCashfreeSetting,
            updateCashfreeSetting,
            'Cashfree Payment settings saved successfully!',
            'Failed to save Cashfree Payment settings'
        );

    const handleCcavenueSubmit = (e) =>
        handleSubmit(
            e,
            ccavenueSettings,
            ccavenueForm,
            createCcavenueSetting,
            updateCcavenueSetting,
            'CCAvenue Payment settings saved successfully!',
            'Failed to save CCAvenue Payment settings'
        );

    const handleCashfreePayoutSubmit = (e) =>
        handleSubmit(
            e,
            cashfreePayoutSettings,
            cashfreePayoutForm,
            createCashfreePayoutSetting,
            updateCashfreePayoutSetting,
            'Cashfree Payout settings saved successfully!',
            'Failed to save Cashfree Payout settings'
        );

    const handleRazorpayPayoutSubmit = (e) =>
        handleSubmit(
            e,
            razorpayPayoutSettings,
            razorpayPayoutForm,
            createRazorpayPayoutSetting,
            updateRazorpayPayoutSetting,
            'Razorpay Payout settings saved successfully!',
            'Failed to save Razorpay Payout settings'
        );

    // ==================== RENDER FUNCTIONS (No changes needed here, as the logic is in handlers) ====================

    // Render Payment Tabs
    const renderPaymentTab = () => (
        <div className="tab-pane fade show active">
            {/* Payment Gateway Tabs Navigation */}
            <ul className="nav nav-pills mb-3" role="tablist">
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill mr-2  ${activePaymentTab === 'razorpay' ? 'btn-info active' : 'btn-secondary'}`}
                             
                        onClick={() => setActivePaymentTab('razorpay')}
                        disabled={modalLoading}
                    >
                        <i className="fas fa-credit-card me-2"></i>
                        Razorpay
                        {razorpayLoading && <span className="spinner-border spinner-border-sm ms-2"></span>}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill mr-2  ${activePaymentTab === 'cashfree' ? ' btn-info active' : 'btn-secondary'}`}
                        
                        onClick={() => setActivePaymentTab('cashfree')}
                        disabled={modalLoading}
                    >
                        <i className="fas fa-wallet me-2"></i>
                        Cashfree
                        {cashfreeLoading && <span className="spinner-border spinner-border-sm ms-2"></span>}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill ${activePaymentTab === 'ccavenue' ? 'btn-info active' : 'btn-secondary'}`}
                        onClick={() => setActivePaymentTab('ccavenue')}
                        disabled={modalLoading}
                    >
                        <i className="fas fa-university me-2"></i>
                        CCAvenue
                        {ccavenueLoading && <span className="spinner-border spinner-border-sm ms-2"></span>}
                    </button>
                </li>
            </ul>

            <div className="tab-content">
                {activePaymentTab === 'razorpay' && renderRazorpayTab()}
                {activePaymentTab === 'cashfree' && renderCashfreeTab()}
                {activePaymentTab === 'ccavenue' && renderCcavenueTab()}
            </div>
        </div>
    );

    // Render Payout Tabs
    const renderPayoutTab = () => (
        <div className="tab-pane fade show active">
            {/* Payout Gateway Tabs Navigation */}
            <ul className="nav nav-pills mb-3" role="tablist">
                <li className="nav-item">
                    <button
 className={`nav-link rounded-pill mr-2 ${activePayoutTab === 'razorpay' ? 'btn-info active' : 'btn-secondary'}`}                        onClick={() => setActivePayoutTab('razorpay')}
                        disabled={modalLoading}
                    >
                        <i className="fas fa-money-bill-wave me-2 "></i>
                        Razorpay Payout
                        {razorpayPayoutLoading && <span className="spinner-border spinner-border-sm ms-2"></span>}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill ${activePayoutTab === 'cashfree' ? 'btn-info active' : 'btn-secondary'}`}
                        onClick={() => setActivePayoutTab('cashfree')}
                        disabled={modalLoading}
                    >
                        <i className="fas fa-money-bill-wave me-2"></i>
                        Cashfree Payout
                        {cashfreePayoutLoading && <span className="spinner-border spinner-border-sm ms-2"></span>}
                    </button>
                </li>
            </ul>

            <div className="tab-content">
                {activePayoutTab === 'razorpay' && renderRazorpayPayoutTab()}
                {activePayoutTab === 'cashfree' && renderCashfreePayoutTab()}
            </div>
        </div>
    );

    // Render Razorpay Payment Tab
    const renderRazorpayTab = () => (
        <div className="tab-pane fade show active">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-4">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-credit-card me-2 text-primary"></i>
                                Razorpay Payment Configuration
                            </h5>
                            
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleRazorpaySubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-link me-1 text-info"></i>
                                                Razorpay URL *
                                            </label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={razorpayForm.field1}
                                                onChange={(e) => handleRazorpayChange('field1', e.target.value)}
                                                required
                                                disabled={modalLoading || razorpayLoading}
                                                placeholder="https://api.razorpay.com"
                                            />
                                            <div className="form-text">
                                                Razorpay API base URL
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-key me-1 text-warning"></i>
                                                Razorpay Key ID *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={razorpayForm.field2}
                                                onChange={(e) => handleRazorpayChange('field2', e.target.value)}
                                                required
                                                disabled={modalLoading || razorpayLoading}
                                                placeholder="Enter Razorpay Key ID"
                                            />
                                            <div className="form-text">
                                                Your Razorpay Key ID from the dashboard
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-lock me-1 text-danger"></i>
                                                Razorpay Key Secret *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={razorpayForm.field3}
                                                onChange={(e) => handleRazorpayChange('field3', e.target.value)}
                                                required
                                                disabled={modalLoading || razorpayLoading}
                                                placeholder="Enter Razorpay Key Secret"
                                            />
                                            <div className="form-text">
                                                Your Razorpay Key Secret from the dashboard
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-shield-alt me-1 text-success"></i>
                                                Webhook Secret
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={razorpayForm.field5}
                                                onChange={(e) => handleRazorpayChange('field5', e.target.value)}
                                                disabled={modalLoading || razorpayLoading}
                                                placeholder="Enter Webhook Secret"
                                            />
                                            <div className="form-text">
                                                Webhook secret for payment verification (optional)
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={razorpayForm.field4}
                                                onChange={(e) => handleRazorpayChange('field4', e.target.value)}
                                                disabled={modalLoading || razorpayLoading}
                                            >
                                                <option value="0">Disabled</option>
                                                <option value="1">Enabled</option>
                                            </select>
                                            <div className="form-text">
                                                Enable or disable Razorpay payments
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {razorpayError && (
                                    <div className="alert alert-danger">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {razorpayError}
                                    </div>
                                )}

                                <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                    <button
                                        type="submit"
                                        className="save-button"
                                        disabled={razorpayLoading || modalLoading}
                                    >
                                        {razorpayLoading || modalLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                {razorpaySettings && razorpaySettings.length > 0 ? 'Update Razorpay Settings' : 'Save Razorpay Settings'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render Cashfree Payment Tab
    const renderCashfreeTab = () => (
        <div className="tab-pane fade show active">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-4">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-wallet me-2 text-info"></i>
                                Cashfree Payment Configuration
                            </h5>
                          
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleCashfreeSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-id-badge me-1 text-warning"></i>
                                                App ID *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={cashfreeForm.field1}
                                                onChange={(e) => handleCashfreeChange('field1', e.target.value)}
                                                required
                                                disabled={modalLoading || cashfreeLoading}
                                                placeholder="Enter App ID"
                                            />
                                            <div className="form-text">
                                                Your Cashfree App ID
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-lock me-1 text-danger"></i>
                                                Secret Key *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={cashfreeForm.field2}
                                                onChange={(e) => handleCashfreeChange('field2', e.target.value)}
                                                required
                                                disabled={modalLoading || cashfreeLoading}
                                                placeholder="Enter Secret Key"
                                            />
                                            <div className="form-text">
                                                Your Cashfree Secret Key
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={cashfreeForm.field3}
                                                onChange={(e) => handleCashfreeChange('field3', e.target.value)}
                                                disabled={modalLoading || cashfreeLoading}
                                            >
                                                <option value="0">Disabled</option>
                                                <option value="1">Enabled</option>
                                            </select>
                                            <div className="form-text">
                                                Enable or disable Cashfree payments
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {cashfreeError && (
                                    <div className="alert alert-danger">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {cashfreeError}
                                    </div>
                                )}

                                <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                    <button
                                        type="submit"
                                        className="save-button"
                                        disabled={cashfreeLoading || modalLoading}
                                    >
                                        {cashfreeLoading || modalLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                {cashfreeSettings && cashfreeSettings.length > 0 ? 'Update Cashfree Settings' : 'Save Cashfree Settings'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render CCAvenue Tab
    const renderCcavenueTab = () => (
        <div className="tab-pane fade show active">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-4">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-university me-2 text-success"></i>
                                CCAvenue Payment Configuration
                            </h5>
                        
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleCcavenueSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-id-card me-1 text-warning"></i>
                                                Merchant ID *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={ccavenueForm.field1}
                                                onChange={(e) => handleCcavenueChange('field1', e.target.value)}
                                                required
                                                disabled={modalLoading || ccavenueLoading}
                                                placeholder="Enter Merchant ID"
                                            />
                                            <div className="form-text">
                                                Your CCAvenue Merchant ID
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-code me-1 text-primary"></i>
                                                Access Code *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={ccavenueForm.field2}
                                                onChange={(e) => handleCcavenueChange('field2', e.target.value)}
                                                required
                                                disabled={modalLoading || ccavenueLoading}
                                                placeholder="Enter Access Code"
                                            />
                                            <div className="form-text">
                                                Access Code provided by CCAvenue
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-cog me-1 text-info"></i>
                                                Working Key *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={ccavenueForm.field3}
                                                onChange={(e) => handleCcavenueChange('field3', e.target.value)}
                                                required
                                                disabled={modalLoading || ccavenueLoading}
                                                placeholder="Enter Working Key"
                                            />
                                            <div className="form-text">
                                                32-character Working Key from CCAvenue
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={ccavenueForm.field4}
                                                onChange={(e) => handleCcavenueChange('field4', e.target.value)}
                                                disabled={modalLoading || ccavenueLoading}
                                            >
                                                <option value="0">Disabled</option>
                                                <option value="1">Enabled</option>
                                            </select>
                                            <div className="form-text">
                                                Enable or disable CCAvenue payments
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {ccavenueError && (
                                    <div className="alert alert-danger">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {ccavenueError}
                                    </div>
                                )}
                                <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                    <button
                                        type="submit"
                                        className="save-button"
                                        disabled={ccavenueLoading || modalLoading}
                                    >
                                        {ccavenueLoading || modalLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                {ccavenueSettings && ccavenueSettings.length > 0 ? 'Update CCAvenue Settings' : 'Save CCAvenue Settings'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render Razorpay Payout Tab
    const renderRazorpayPayoutTab = () => (
        <div className="tab-pane fade show active">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-4">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-money-bill-wave me-2 text-primary"></i>
                                Razorpay Payout Configuration
                            </h5>
                          
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleRazorpayPayoutSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-link me-1 text-info"></i>
                                                Razorpay Payout URL *
                                            </label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={razorpayPayoutForm.field1}
                                                onChange={(e) => handleRazorpayPayoutChange('field1', e.target.value)}
                                                required
                                                disabled={modalLoading || razorpayPayoutLoading}
                                                placeholder="https://api.razorpay.com"
                                            />
                                            <div className="form-text">
                                                Razorpay Payout API base URL
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-key me-1 text-warning"></i>
                                                Razorpay Key ID *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={razorpayPayoutForm.field2}
                                                onChange={(e) => handleRazorpayPayoutChange('field2', e.target.value)}
                                                required
                                                disabled={modalLoading || razorpayPayoutLoading}
                                                placeholder="Enter Razorpay Key ID"
                                            />
                                            <div className="form-text">
                                                Your Razorpay Payout Key ID
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-lock me-1 text-danger"></i>
                                                Razorpay Key Secret *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={razorpayPayoutForm.field3}
                                                onChange={(e) => handleRazorpayPayoutChange('field3', e.target.value)}
                                                required
                                                disabled={modalLoading || razorpayPayoutLoading}
                                                placeholder="Enter Razorpay Key Secret"
                                            />
                                            <div className="form-text">
                                                Your Razorpay Payout Key Secret
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-shield-alt me-1 text-success"></i>
                                                Webhook Secret
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={razorpayPayoutForm.field5}
                                                onChange={(e) => handleRazorpayPayoutChange('field5', e.target.value)}
                                                disabled={modalLoading || razorpayPayoutLoading}
                                                placeholder="Enter Webhook Secret"
                                            />
                                            <div className="form-text">
                                                Webhook secret for payout verification (optional)
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={razorpayPayoutForm.field4}
                                                onChange={(e) => handleRazorpayPayoutChange('field4', e.target.value)}
                                                disabled={modalLoading || razorpayPayoutLoading}
                                            >
                                                <option value="0">Disabled</option>
                                                <option value="1">Enabled</option>
                                            </select>
                                            <div className="form-text">
                                                Enable or disable Razorpay Payout
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {razorpayPayoutError && (
                                    <div className="alert alert-danger">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {razorpayPayoutError}
                                    </div>
                                )}

                                <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                    <button
                                        type="submit"
                                        className="save-button"
                                        disabled={razorpayPayoutLoading || modalLoading}
                                    >
                                        {razorpayPayoutLoading || modalLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                {razorpayPayoutSettings && razorpayPayoutSettings.length > 0 ? 'Update Razorpay Payout Settings' : 'Save Razorpay Payout Settings'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render Cashfree Payout Tab
    const renderCashfreePayoutTab = () => (
        <div className="tab-pane fade show active">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-4">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-money-bill-wave me-2 text-warning"></i>
                                Cashfree Payout Configuration
                            </h5>
                          
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleCashfreePayoutSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-id-card me-1 text-primary"></i>
                                                Client ID *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={cashfreePayoutForm.field1}
                                                onChange={(e) => handleCashfreePayoutChange('field1', e.target.value)}
                                                required
                                                disabled={modalLoading || cashfreePayoutLoading}
                                                placeholder="Enter Client ID"
                                            />
                                            <div className="form-text">
                                                Your Cashfree Payout Client ID
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                <i className="fas fa-lock me-1 text-danger"></i>
                                                Client Secret *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={cashfreePayoutForm.field2}
                                                onChange={(e) => handleCashfreePayoutChange('field2', e.target.value)}
                                                required
                                                disabled={modalLoading || cashfreePayoutLoading}
                                                placeholder="Enter Client Secret"
                                            />
                                            <div className="form-text">
                                                Your Cashfree Payout Client Secret
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={cashfreePayoutForm.field3}
                                                onChange={(e) => handleCashfreePayoutChange('field3', e.target.value)}
                                                disabled={modalLoading || cashfreePayoutLoading}
                                            >
                                                <option value="0">Disabled</option>
                                                <option value="1">Enabled</option>
                                            </select>
                                            <div className="form-text">
                                                Enable or disable Cashfree Payout
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {cashfreePayoutError && (
                                    <div className="alert alert-danger">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {cashfreePayoutError}
                                    </div>
                                )}

                                <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                    <button
                                        type="submit"
                                        className="save-button"
                                        disabled={cashfreePayoutLoading || modalLoading}
                                    >
                                        {cashfreePayoutLoading || modalLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                {cashfreePayoutSettings && cashfreePayoutSettings.length > 0 ? 'Update Payout Settings' : 'Save Payout Settings'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ==================== MAIN RENDER ====================

    return (
        <div className="page-wrapper ml-0">
            <div className="content-container">
<div className="page-header filter-bar rounded">
    <div className="row align-items-center">
        <div className="col">
            <h4 className="page-title">Payment & Payout Settings</h4>
            <ul className="breadcrumb">
                <li className="breadcrumb-item">
                    <Link to="/doctor/dashboard">Admin Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">
                    Configure and manage payment and payout gateway
                </li>
            </ul>
            <div className="mt-2">
                <small className="opacity-75">Hospital ID: {getHospitalId()}</small>
            </div>
        </div>
    </div>
</div>

          
                        <div className='card'>
                        <div className="card-body pt-3">
                            {/* Main Tabs Navigation */}
                            <ul className="nav nav-tabs" role="tablist">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeMainTab === 'payment' ? 'active' : ''}`}
                                        onClick={() => setActiveMainTab('payment')}
                                        disabled={modalLoading}
                                    >
                                        <i className="fas fa-credit-card me-2"></i>
                                        Payment Gateways
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeMainTab === 'payout' ? 'active' : ''}`}
                                        onClick={() => setActiveMainTab('payout')}
                                        disabled={modalLoading}
                                    >
                                        <i className="fas fa-money-bill-wave me-2"></i>
                                        Payout Gateways
                                    </button>
                                </li>
                            </ul>

                            <div className="tab-content p-3">
                                {activeMainTab === 'payment' && renderPaymentTab()}
                                {activeMainTab === 'payout' && renderPayoutTab()}
                            </div>
                        </div>
                   </div>
                

                    {/* Message Modal */}
                    <MessageModal
                        show={modalShow}
                        onClose={handleModalClose}
                        title={modalTitle}
                        message={modalMessage}
                        variant={modalVariant}
                        showConfirm={false}
                        cancelText="Close"
                        loading={modalLoading}
                    />
            </div>
        </div>
    );
};

export default PaymentSettings;